import { push } from "connected-react-router";
import * as queryString from "./queryString";
import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router";

export function validateUrl(url: string): [boolean, string?] {
    if (!url) {
        return [true, undefined];
    }
    const schemeIndex = url.indexOf("://");
    if (schemeIndex < 0) {
        return [false, "Invalid URL"];
    }
    const scheme = url.substring(0, schemeIndex);
    // Accepted Schemes
    const ACCEPTED_SCHEMES = ["https", "wss"];

    if (!ACCEPTED_SCHEMES.includes(scheme)) {
        return [false, `The URL must start with https:// or wss://`];
    }

    try {
        new URL(url);
        return [true, undefined];
    } catch {
        return [false, "Invalid URL"];
    }
    // return [true, undefined];
}

/**
 * Note: The set function here does not work
 *
 * @param key
 * @returns
 */
export function useQueryState<TKey extends string>(key: TKey): [string | undefined, (val: string | undefined) => void] {
    const location = useLocation();
    const current = useMemo(() => {
        const qs = queryString.parse(location.search);
        const rawValue = qs[key];
        return Array.isArray(rawValue) ? rawValue[0] : rawValue;
    }, [location, key]);

    const navigate = useNavigateQuery();
    const nav = useCallback((val: string) => {
        console.log(`nav ${key}=${val}`);
        navigate({ [key]: val });
    }, [navigate, key]);

    return useMemo(() => [current, nav], [current, nav]);
}

export function useQueryNumberState<TKey extends string>(key: TKey, defaultValue?: number): [number | undefined, (val: number | undefined) => void] {
    const [rawValue, setRawValue] = useQueryState(key);
    const value = useMemo<number | undefined>(() => {
        const parsed = parseFloat(rawValue);
        if (isNaN(parsed)) {
            return defaultValue;
        }
        if (parsed === undefined) {
            return defaultValue;
        }
        return parsed;
    }, [rawValue, defaultValue]);

    const setValue = useCallback((val: number) => setRawValue(val.toString()), [setRawValue]);

    return useMemo(() => [value, setValue], [value, setValue]);
}

export type MultiValueQSMapper<T> = {
    readonly [key in keyof T]: (src: string | readonly string[] | null | undefined) => T[key];
};

export type SingleValueQSMapper<T> = {
    readonly [key in keyof T]: (src: string | null | undefined) => T[key];
};

export function useMultiQueryParams<T>(mapper: MultiValueQSMapper<T>): T {
    const location = useLocation();
    const qs = queryString.parse(location.search);

    const res: T = {} as T;
    for (const key in mapper) {
        if (mapper.hasOwnProperty(key)) {
            const raw = qs[key];
            res[key] = mapper[key](raw);
        }
    }

    return res;
}

export function useSingleHashStringParam(key: string): string {
    const location = useLocation();
    const qs = queryString.parse(location.hash);
    const raw = qs[key];
    if (Array.isArray(raw)) {
        return raw.join(",");
    }
    return raw;
}

/**
 * Caution, not sure how to properly use this yet since it tends to cause rapid rerendering.
 * @param mapper
 * @returns
 */
export function useSingleQueryParams<T>(mapper: SingleValueQSMapper<T>): T {
    const location = useLocation();
    const qs = queryString.parse(location.search);

    const res: T = {} as T;
    for (const key in mapper) {
        if (mapper.hasOwnProperty(key)) {
            const rawValue = qs[key];
            const raw = Array.isArray(rawValue) ? rawValue[0] : rawValue;
            res[key] = mapper[key](raw);
        }
    }

    return res;
}

export function useEnsureQueryParams<T>(args: { [key: string]: () => string | null }, mapper: SingleValueQSMapper<T>): T {
    const location = useLocation();
    const dispatch = useDispatch();
    const qs = queryString.parse(location.search);
    let dirty = false;

    for (const key in args) {
        if (args.hasOwnProperty(key)) {
            const raw = qs[key];
            if (!raw || (Array.isArray(raw) && !raw.length)) {
                const defaultValue = args[key]();
                if (defaultValue !== undefined) {
                    qs[key] = defaultValue;
                    dirty = true;
                }
            }
        }
    }

    const res: T = {} as T;
    for (const key in mapper) {
        if (mapper.hasOwnProperty(key)) {
            const rawValue = qs[key];
            const raw = Array.isArray(rawValue) ? rawValue[0] : rawValue;
            res[key] = mapper[key](raw);
        }
    }

    if (dirty) {
        const str = queryString.stringify(qs);
        dispatch(push(`${location.pathname}?${str}`));
    }

    return res;
}

export function useNavigateQuery<T>(): (obj: Partial<T>) => void {
    const location = useLocation();
    const dispatch = useDispatch();
    // const history = useHistory();

    const navigate = useCallback((obj: Partial<T>) => {
        const qs = queryString.parseUrl(`${location.pathname}?${location.search}`);
        const res = queryString.stringify({
            ...qs.query,
            ...obj
        });
        // history.replace(`${location.pathname}?${res}`);
        dispatch(push(`${qs.url}?${res}`));
    }, [dispatch, location.pathname, location.search]);

    return navigate;
}

export function useNavigateQueryLinkBuilder<T>() {
    const location = useLocation();

    function navigate(obj: Partial<T>) {
        const qs = queryString.parseUrl(`${location.pathname}?${location.search}`);
        const res = queryString.stringify({
            ...qs.query,
            ...obj
        });
        return (`${qs.url}?${res}`);
    }

    return navigate;
}

export interface IPaginationQueryData {
    readonly endDate: Date;
    readonly fromIndex: number;
    readonly pageSize: number;
}

export interface IUrlPagination extends IPaginationQueryData {
    readonly page: number;
    handlePageChange(page: number): void;
    handlePageSizeChange(pageSize: number): void;
}

export function usePaginationQueryData(defaults: { readonly pageSize: number, readonly endDate?: Date | null }): IPaginationQueryData {
    const { endDate } = defaults;
    return useEnsureQueryParams({
        endDate: () => endDate === null ? undefined : (endDate || new Date()).toISOString(),
        fromIndex: () => "0",
        pageSize: () => defaults.pageSize.toString()
    }, {
        endDate: raw => raw && new Date(raw),
        fromIndex: raw => { const n = parseInt(raw, 10); return isNaN(n) ? 0 : n; },
        pageSize: raw => { const n = parseInt(raw, 10); return isNaN(n) ? defaults.pageSize : n; },
    });
}

export function usePageChange(pageSize: number) {
    const navigate = useNavigateQuery();
    return useCallback((page: number) => {
        navigate({ fromIndex: ((page - 1) * pageSize).toString() });
    }, [pageSize, navigate]);
}


export function usePageSizeChange() {
    const navigate = useNavigateQuery();
    return useCallback((pageSize: number) => {
        navigate({ pageSize: pageSize.toString() });
    }, [navigate]);
}

export function useUrlPagination(defaults: { readonly pageSize: number, readonly endDate?: Date | null }): IUrlPagination {
    const query = usePaginationQueryData(defaults);
    return {
        ...query,
        handlePageChange: usePageChange(query.pageSize),
        handlePageSizeChange: usePageSizeChange(),
        page: Math.floor((query.fromIndex / query.pageSize)) + 1,
    };
}

export interface MatchConfidenceQueryParams {
    ltConfidence?: number;
    lteConfidence?: number;
    gtConfidence?: number;
    gteConfidence?: number;
}

/**
 * Pulls the match confidence values from the query string params
 *
 * @returns
 */
export function useMatchConfidenceQuery(): MatchConfidenceQueryParams {
    return useEnsureQueryParams({
        ltConfidence: () => undefined,
        lteConfidence: () => undefined,
        gtConfidence: () => undefined,
        gteConfidence: () => undefined,
    }, {
        ltConfidence: raw => raw ? parseFloat(raw) : undefined,
        lteConfidence: raw => raw ? parseFloat(raw) : undefined,
        gtConfidence: raw => raw ? parseFloat(raw) : undefined,
        gteConfidence: raw => raw ? parseFloat(raw) : undefined,
    });
}